<template>
  <CBox :bg="['#F1F2F4', 'var(--colors-light-primary-light-gray)']" minHeight="100vh" width="100vw" position="sticky">
    <OmtLayoutContainer :bg="['#F1F2F4', '#FFFF']">
      <template v-if="hydrated || hydrating">
        <OmtRow
          bg="#FFF"
          :mt="['-24px', '-40px']"
          :mx="['-16px', '-40px']"
          :px="['16px', '24px']"
          :py="['16px', '24px']"
          border-bottom-left-radius="8px"
          border-bottom-right-radius="8px"
        >
          <CFlex direction="column" grid-gap="16px">
            <OmtInput
              variant="outline"
              borderRadius="8px"
              placeholder="Busque a especialidade ou médico"
              icon-left="search"
              :icon-right="inputText.length > 0 ? 'times' : 'microphone'"
              :iconLeftClick="onSearch"
              :iconRightClick="inputText.length > 0 ? clearSearch : onSearchVoice"
              v-model="inputText"
              v-on:keydown.enter.prevent="onSearch"
              :borderColor="searchInputBorderColor"
              :_focus="{ borderColor: searchInputBorderColor, boxShadow: 'initial' }"
              @focus="isSearchInputFocused = true"
              @blur="isSearchInputFocused = false"
              data-tour="busca-rede-home-search"
            />
          </CFlex>
        </OmtRow>
      </template>

      <template v-else>
        <OmtRow mb="16px">
          <OmtSelect placeholder="Selecione um plano" @change="e => setPlano(e)" mb="24px" :initialValue="plano">
            <option v-for="(p, index) in planos" :key="`${p.Codigo}-${index}`" :value="p.Codigo">
              {{ p.Codigo }}
            </option>
          </OmtSelect>
        </OmtRow>
        <OmtRow mb="16px">
          <OmtSelect placeholder="Ou selecione uma rede" @change="e => setRede(e)" mb="24px" :initialValue="rede">
            <option v-for="(r, index) in redes" :key="`${r.Codigo}-${index}`" :value="r.Codigo">
              {{ r.Descricao }}
            </option>
          </OmtSelect>
        </OmtRow>
      </template>

      <OmtRow :mr="['-16px', '-40px']" :ml="['-16px', '-40px']" px="16px" mt="16px" v-if="hasNoResults">
        <CFlex direction="column" gridGap="16px" w="100%">
          <!-- Card 1 - Nenhum resultado -->
          <CBox bg="#FFFFFF" border-radius="8px" p="16px" w="100%">
            <CFlex direction="column" align="flex-start">
              <CBox
                w="28px"
                h="28px"
                border-radius="50%"
                bg="rgba(255, 116, 0, 0.1)"
                display="flex"
                align-items="center"
                justify-content="center"
                mb="8px"
                flex-shrink="0"
              >
                <OmtIcons name="circle-xmark" size="lg" color="#FF7400" />
              </CBox>
              <OmtTypography as="h3-bold" font-size="14px" color="#4E4E4E" line-height="20px" mb="8px">
                Nenhum resultado para “{{ searchedTerm }}”
              </OmtTypography>
              <OmtTypography as="p1" font-size="14px" font-weight="400" color="#4E4E4E" line-height="20px" mt="8px">
                Verifique o termo digitado e refaça a busca.
              </OmtTypography>
            </CFlex>
          </CBox>

          <!-- Card 2 - Indicação médica -->
          <CBox bg="#FFFFFF" border-radius="8px" p="16px" w="100%">
            <CFlex direction="column" align="flex-start">
              <CBox
                w="28px"
                h="28px"
                border-radius="50%"
                bg="rgba(28, 160, 255, 0.1)"
                display="flex"
                align-items="center"
                justify-content="center"
                mb="8px"
                flex-shrink="0"
              >
                <OmtIcons name="lightbulb" size="lg" color="#1CA0FF" />
              </CBox>
              <OmtTypography as="h3-bold" font-size="14px" color="#4E4E4E" line-height="20px" mb="8px">
                Não encontrou o que precisa?
              </OmtTypography>
              <OmtTypography as="p1" font-size="14px" font-weight="400" color="#4E4E4E" line-height="20px" mt="8px">
                A Omint encontra e indica o profissional ideal.
              </OmtTypography>
              <OmtButton mt="16px" height="40px" px="16px" border-radius="40px" @click="requestMedicalIndication">
                <OmtTypography as="h3-bold" color="#fff">Pedir indicação médica</OmtTypography>
              </OmtButton>
            </CFlex>
          </CBox>
        </CFlex>
      </OmtRow>

      <OmtRow
        :mt="['0px', '24px']"
        :mx="['-16px', '-40px']"
        :px="['16px', '24px']"
        :py="['16px', '24px']"
        :bg="['#F1F2F4', '#FFFF']"
        :borderRadius="['0', '8px']"
        v-else-if="loaded && !hasHomeItems"
      >
        <CBox bg="#FFFFFF" border-radius="8px" p="16px" w="100%">
          <CFlex direction="column" align="center" text-align="center">
            <OmtTypography as="h3-bold" font-size="16px" color="#4E4E4E" line-height="24px" mb="8px">
              Nenhum item disponível
            </OmtTypography>
            <OmtTypography as="p1" font-size="14px" font-weight="400" color="#4E4E4E" line-height="20px">
              No momento, não há informações ou categorias disponíveis para exibição.
            </OmtTypography>
          </CFlex>
        </CBox>
      </OmtRow>

      <OmtRow
        :mt="['0px', '24px']"
        :mx="['-16px', '-40px']"
        :px="['16px', '24px']"
        :py="['16px', '24px']"
        :bg="['#F1F2F4', '#FFFF']"
        :borderRadius="['0', '8px']"
        v-else
      >
        <CBox
          v-if="loaded"
          display="grid"
          grid-template-columns="repeat(3, minmax(0, 1fr))"
          grid-gap="16px"
          width="100%"
        >
          <CBox
            v-for="(tile, index) in categoryTiles"
            :key="index"
            @click="onTileClick(tile)"
            cursor="pointer"
            border="0.5px solid var(--colors-light-secondary-divider)"
            border-radius="8px"
            px="8px"
            py="16px"
            display="flex"
            flex-direction="column"
            bg="#FFFFFF"
            :maxW="'100%'"
          >
            <CFlex align-items="center" justify-content="space-between" mb="16px">
              <img
                v-if="tile.Codigo === 'hospital'"
                :src="baseUrl + '/images/omint/hospitals.svg'"
                alt="Hospital"
                style="width: 1em; height: 1em"
              />
              <OmtIcons v-else :name="getIconName(tile.Codigo)" size="1x" />
            </CFlex>
            <OmtTypography as="p1"> {{ tile.title }} </OmtTypography>
          </CBox>
        </CBox>
      </OmtRow>

      <template v-if="!hasNoResults && hasHomeItems">
        <OmtRow
          :mt="['0', '24px']"
          :mx="['-16px', '-40px']"
          v-if="homeRecommendations.length"
          :bg="['#F1F2F4', '#FFFF']"
          px="24px"
          :py="['8px', '24px']"
          :borderRadius="['0', '8px']"
        >
          <CBox>
            <OmtTypography as="l2">Recomendações</OmtTypography>
            <CBox mt="16px" display="flex" flex-wrap="wrap">
              <CBox
                bg="#FFFFFF"
                v-for="(recommendation, index) in homeRecommendations"
                :key="`recommendation-${index}`"
                cursor="pointer"
                mr="8px"
                mb="8px"
                width="fit-content"
                border-radius="40px"
                px="16px"
                py="4px"
                border="0.5px solid var(--colors-light-secondary-divider)"
                @click="handleRecommendation(recommendation)"
                :data-tour="isFavoritosRecommendation(recommendation) ? 'busca-rede-favoritos' : undefined"
              >
                <OmtTypography as="p1">{{ recommendation.Titulo }}</OmtTypography>
              </CBox>
            </CBox>
          </CBox>
        </OmtRow>

        <OmtRow
          :mt="['0', '24px']"
          :mx="['-16px', '-40px']"
          px="24px"
          :py="['8px', '24px']"
          v-if="homeFrequentSearches.length"
          :bg="['#F1F2F4', '#FFFF']"
          :borderRadius="['0', '8px']"
        >
          <CBox>
            <OmtTypography as="l2">Buscas frequentes</OmtTypography>
            <CBox mt="16px" display="flex" flex-wrap="wrap">
              <CBox
                bg="#FFFFFF"
                v-for="(search, index) in homeFrequentSearches"
                :key="`search-${index}`"
                cursor="pointer"
                mr="8px"
                mb="8px"
                width="fit-content"
                border-radius="40px"
                px="16px"
                py="4px"
                border="0.5px solid var(--colors-light-secondary-divider)"
                @click="searchByTerm(search.Termo)"
              >
                <OmtTypography as="p1">{{ search.Termo }}</OmtTypography>
              </CBox>
            </CBox>
          </CBox>
        </OmtRow>

        <OmtRow
          mt="24px"
          :desktopStart="5"
          :desktopEnd="8"
          v-if="loaded && (hydrated || hydrating)"
          @click="setDeactivated"
          cursor="pointer"
        >
          <OmtTypography as="p1" text-decoration="underline">
            Consultar credenciados <span v-show="deactivated">ativos</span><span v-show="!deactivated">inativos</span>
          </OmtTypography>
        </OmtRow>
      </template>

      <ModalSearchAddress :is-open="showLocationModal" @close="closeLocationModal" @select="handleAddressSelection" />

      <ModalSearchOtherAddress
        :is-open="showOtherAddressModal"
        @close="closeOtherAddressModal"
        @apply="handleOtherAddressSelection"
      />

      <Onboard v-if="shouldOpenOnboard" onboard="affiliate-search-onboard" />
    </OmtLayoutContainer>
  </CBox>
</template>

<script>
import LocationCard from "@/components/Organisms/AffiliateSearch/LocationCard";
import ModalSearchAddress from "@/components/Organisms/AffiliateSearch/ModalSearchAddress";
import ModalSearchOtherAddress from "@/components/Organisms/AffiliateSearch/ModalSearchOtherAddress";
import { capitalizeWords, isMobile } from "@/utils/utils";
import { maybeResumeBuscaRedeTourOnAffiliateHome } from "@/utils/buscaRedeTour";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "AffiliateHome",
  components: {
    ModalSearchAddress,
    ModalSearchOtherAddress,
    LocationCard
  },
  data() {
    return {
      inputText: "",
      loaded: false,
      showLocationModal: false,
      showOtherAddressModal: false,
      isSearchInputFocused: false,
      hasNoResults: false,
      searchedTerm: ""
    };
  },
  computed: {
    ...mapGetters("saude", ["baseUrl"]),
    ...mapState("saude", ["hydrated", "hydrating"]),
    ...mapState("affiliate", [
      "homeCategories",
      "homeRecommendations",
      "homeFrequentSearches",
      "enderecoSelecionado",
      "locationDisabled",
      "plano",
      "planos",
      "rede",
      "redes",
      "deactivated",
      "title"
    ]),
    isMobile() {
      return isMobile();
    },
    searchInputBorderColor() {
      return this.isSearchInputFocused
        ? "var(--colors-light-primary-navy-blue)"
        : "var(--colors-light-secondary-divider)";
    },
    hasHomeItems() {
      return (
        (this.categoryTiles && this.categoryTiles.length > 0) ||
        (this.homeRecommendations && this.homeRecommendations.length > 0) ||
        (this.homeFrequentSearches && this.homeFrequentSearches.length > 0)
      );
    },
    locationTitle() {
      if (this.enderecoSelecionado) {
        if (
          (this.enderecoSelecionado.Tipo === "atual" || this.enderecoSelecionado.Tipo === "Atual") &&
          this.enderecoSelecionado.Titulo
        ) {
          return this.enderecoSelecionado.Titulo;
        }
        return this.enderecoSelecionado.Titulo || "Localização";
      }
      return "Localização";
    },
    locationSubtitle() {
      if (this.enderecoSelecionado) {
        if (this.enderecoSelecionado.Tipo === "atual" || this.enderecoSelecionado.Tipo === "Atual") {
          return "Buscando pela sua localização atual";
        }
        return this.enderecoSelecionado.Descricao || this.formatLocationSubtitle(this.enderecoSelecionado);
      }
      return "";
    },
    isCurrentLocationDisabled() {
      if (!this.locationDisabled) return false;

      const isEnderecoAtual =
        this.enderecoSelecionado &&
        (this.enderecoSelecionado.Tipo === "atual" || this.enderecoSelecionado.Tipo === "Atual");

      return !!isEnderecoAtual;
    },
    categoryTiles() {
      return [...this.homeCategories]
        .sort((a, b) => (a.Ordem ?? 0) - (b.Ordem ?? 0))
        .map(category => ({
          title: capitalizeWords(category.Titulo),
          icon: category.Icone,
          ...category
        }));
    }
  },
  methods: {
    ...mapMutations("affiliate", [
      "SET_LOCATION_TYPE",
      "SET_DEACTIVATED",
      "SET_TIPO_ATENDIMENTO",
      "SET_REDE",
      "SET_PLANO",
      "SET_GROUP",
      "SET_ENDERECO_SELECIONADO"
    ]),
    ...mapActions("affiliate", [
      "fetchGroups",
      "fetchMatchesByText",
      "reset",
      "startRecording",
      "saveSearchHistory",
      "getAddressSearch",
      "requestUserLocation"
    ]),
    isFavoritosRecommendation(recommendation) {
      const codigo = String(recommendation?.TipoAcao || "").toLowerCase();
      const titulo = String(recommendation?.Titulo || "").toLowerCase();
      return codigo === "favoritos" || codigo === "seus-favoritos" || titulo.includes("favorito");
    },
    async onSearch() {
      if (this.inputText.length < 3) {
        if (this.inputText.length === 0) {
          this.hasNoResults = false;
          this.searchedTerm = "";
        }
        return;
      }
      await this.$root.$emit("show-loading");
      this.hasNoResults = false;
      this.searchedTerm = "";
      await this.saveSearchHistory(this.inputText);
      await this.reset();

      try {
        const response = await this.fetchMatchesByText(this.inputText);
        await this.$root.$emit("hide-loading");
        if (response > 0) {
          this.SET_LOCATION_TYPE("Busca por texto");
          await this.$router.push({ name: "affiliate.docs" });
        } else {
          this.hasNoResults = true;
          this.searchedTerm = this.inputText;
        }
      } catch (e) {
        await this.$root.$emit("hide-loading");
        this.hasNoResults = true;
        this.searchedTerm = this.inputText;
      }
    },
    onSearchVoice() {
      this.startRecording({
        initialText: this.inputText,
        onUpdate: text => {
          this.inputText = text;
        },
        onError: () => {
          this.$toast({
            description: "Falha ao iniciar reconhecimento de voz.",
            status: "error",
            duration: 8000,
            position: "top"
          });
        },
        onUnsupported: () => {
          this.$toast({
            description: "Seu navegador não suporta reconhecimento de voz.",
            status: "warning",
            duration: 8000,
            position: "top"
          });
        }
      });
    },
    async searchByTerm(term) {
      this.inputText = term;
      await this.onSearch();
    },
    clearSearch() {
      this.inputText = "";
      this.hasNoResults = false;
      this.searchedTerm = "";
    },
    async handleRecommendation(recommendation) {
      const codigo = (recommendation.TipoAcao || "").toLowerCase();

      switch (codigo) {
        case "indicacao-medica":
          this.requestMedicalIndication();
          break;
        case "favoritos":
        case "seus-favoritos":
          this.SET_LOCATION_TYPE("Favoritos");
          this.$router.push({ name: "affiliate.docs", params: { isFavoritos: true } });
          break;
        default:
          this.$toast({
            description: "Recomendação indisponível no momento.",
            status: "info",
            duration: 8000,
            position: "top"
          });
          break;
      }
    },
    setDeactivated() {
      this.SET_DEACTIVATED(!this.deactivated);
      this.setTitle();
    },
    setTitle() {
      this.$root.$emit("change-title-label", this.title);
    },
    setPlano(e) {
      this.SET_REDE("");
      this.SET_PLANO(e);
      this.fetchGroups();
    },
    setRede(e) {
      this.SET_REDE(e);
      this.SET_PLANO("");
      this.fetchGroups();
    },
    onTileClick(item) {
      const codigoRede = item.Rede ?? item.CodigoRede ?? item.codigoRede ?? item.CdRede ?? "";
      const tipoView = item.Codigo ?? item.TipoView ?? "";
      const descricao = item.Titulo ?? item.DescricaoTipoAtendimento ?? "";

      this.SET_REDE(codigoRede);
      this.SET_TIPO_ATENDIMENTO(tipoView);
      this.SET_GROUP(descricao);
      this.SET_LOCATION_TYPE("");
      this.$router.push({ name: "affiliate.specs" });
    },
    getIconName(TipoView) {
      switch (TipoView) {
        case "pronto-atendimento":
          return "hospital";
        case "medicos-clinicas":
          return "stethoscope";
        case "prontsoc":
          return "hospital";
        case "mediclin":
          return "stethoscope";
        case "laborato":
          return "flask";
        case "pshospital":
          return "baby-carriage";
        case "hospital":
          return "hospitals";
        case "dentclin":
          return "tooth";
        case "terapia":
          return "user-doctor";
        case "vacinas":
          return "syringe";
        default:
          return "hospital";
      }
    },
    capitalizeWords(inputString) {
      return inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase();
    },
    normalize(value) {
      return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    },
    formatLocationSubtitle(location) {
      if (location.Descricao) return location.Descricao;
      if (location.Endereco) return location.Endereco;
      const cityState = [location.Cidade, location.Uf].filter(Boolean).join("/");
      return [cityState, location.Cep].filter(Boolean).join(" - ");
    },
    onLocationClick() {
      this.showLocationModal = true;
    },
    closeLocationModal() {
      this.showLocationModal = false;
    },
    openOtherAddressModal() {
      this.showOtherAddressModal = true;
    },
    closeOtherAddressModal() {
      this.showOtherAddressModal = false;
    },
    async handleAddressSelection(payload) {
      if (payload?.type === "outra-cidade") {
        await this.$root.$emit("show-loading");
        try {
          await this.$store.dispatch("affiliate/fetchStatesNetwork");
          this.closeLocationModal();
          this.openOtherAddressModal();
        } finally {
          await this.$root.$emit("hide-loading");
        }
        return;
      }

      this.SET_ENDERECO_SELECIONADO(payload?.data || null);
      this.closeLocationModal();
    },
    handleOtherAddressSelection(payload) {
      if (payload?.type === "outra-cidade-selecionada") {
        this.SET_ENDERECO_SELECIONADO({
          ...payload.data,
          Tipo: "outra-cidade-selecionada"
        });
      }
    },
    requestMedicalIndication() {
      this.$router.push({ name: "indication.form" });
    }
  },
  async mounted() {
    try {
      // Busca credenciada
      this.$root.$emit("change-back-route", "/");
      await this.setTitle();
      this.$root.$emit("show-loading");
      await this.reset();

      // Disparamos a busca de grupos, endereços e o prompt de localização em paralelo
      await Promise.all([this.fetchGroups(), this.getAddressSearch(), this.requestUserLocation()]);

      // Após o carregamento, se não tiver endereçoSelecionado e a localização estiver desabilitada,
      // força a seleção da primeira opção salva válida
      if (this.$store.state.affiliate.locationDisabled) {
        const hasCurrentAsSelected =
          this.enderecoSelecionado &&
          (this.enderecoSelecionado.Tipo === "atual" || this.enderecoSelecionado.Tipo === "Atual");

        if (hasCurrentAsSelected) {
          const addresses = this.$store.state.affiliate.addresses || [];
          const savedAddresses = addresses.filter(endereco => endereco.Tipo !== "atual");
          const fallbackAddress = savedAddresses.find(addr => addr.Tipo !== "outra-cidade") || savedAddresses[0];

          if (fallbackAddress) {
            this.SET_ENDERECO_SELECIONADO(fallbackAddress);
          }
        }
      }
    } finally {
      this.loaded = true;
      this.$root.$emit("hide-loading");
      this.$nextTick(() => {
        maybeResumeBuscaRedeTourOnAffiliateHome(this);
      });
    }
  },
  destroyed() {
    this.$root.$emit("change-back-route", null);
  }
};
</script>
